import {
    Autocomplete,
    Box,
    CircularProgress,
    TextField,
    Typography,
    Paper,
} from '@mui/material';
import { LocationOn as LocationOnIcon } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import debounce from 'lodash/debounce';
import { SxProps, Theme } from '@mui/material/styles';
import SearchIcon from "@mui/icons-material/Search";
import {theme, themeColors} from "../styles/theme";

// Define libraries array outside component to prevent recreation on each render
const googleMapsLibraries = ['places'] as any;

// Google attribution text for TOS compliance
const GOOGLE_ATTRIBUTION = "Powered by Google";

// Interface for the pub result
export interface GooglePubResult {
    name: string;
    address?: string;
    formatted_address?: string;
    google_place_id: string;
    latitude: number;
    longitude: number;
    uid?: string;
}

// Props interface for the component
export interface PubSearchGoogleProps {
    onPubSelect: (pub: GooglePubResult) => void;
    placeholder?: string;
    initialQuery?: string;
    containerSx?: SxProps<Theme>;
    inputSx?: SxProps<Theme>;
    disabled?: boolean;
    showBiasToUserLocation?: boolean;
    onError?: (error: string | React.ReactNode) => void;
    onLoading?: (isLoading: boolean) => void;
    autoFocus?: boolean;
    attributionBottom?: string; // Custom bottom position for Google attribution
    attributionRightEmpty?: string; // Custom right position when search is empty
    attributionRightWithText?: string; // Custom right position when search has text
}

const PubSearchGoogle = ({
    onPubSelect,
    placeholder = "Search for a pub",
    initialQuery = '',
    containerSx = {},
    inputSx = {},
    disabled = false,
    showBiasToUserLocation = true,
    onError,
    onLoading,
    autoFocus = false,
    attributionBottom = '4px',
    attributionRightEmpty = '60px',
    attributionRightWithText = '80px'
}: PubSearchGoogleProps) => {
    // State for search
    const [searchQuery, setSearchQuery] = useState<string>(initialQuery);
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [autocompletePubsLoading, setAutocompletePubsLoading] = useState<boolean>(false);
    
    // References for Google services
    const placesServiceRef = useRef<any | null>(null);
    const autocompleteServiceRef = useRef<any | null>(null);
    
    // Load Google Maps scripts
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: googleMapsLibraries,
    });
    
    // Initialise Google services
    useEffect(() => {
        if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
            try {
                // We need a dummy div for PlacesService to work
                const dummyElement = document.createElement('div');
                placesServiceRef.current = new window.google.maps.places.PlacesService(dummyElement);
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                // console.log('Google Places services initialised');
            } catch (error) {
                console.error('Failed to initialise Places services:', error);
                onError?.('Failed to initialise search service');
            }
        }
    }, [isLoaded, onError]);
    
    // Handle input change
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        if (onError) onError(null);
    }
    
    // Handle search query change with debounce
    const debouncedPlacesSearch = useCallback(
        debounce((query: string) => {
            if (!query || query.length < 3 || !isLoaded || !autocompleteServiceRef.current) {
                setSearchResults([]);
                setAutocompletePubsLoading(false);
                if (onLoading) onLoading(false);
                return;
            }

            // Check if we have stored user location to bias results
            const locationPermission = localStorage.getItem('locationPermissionGranted');
            const storedLat = localStorage.getItem('userLatitude');
            const storedLon = localStorage.getItem('userLongitude');

            const request: any = {
                input: query,
                types: ['establishment'],
            };

            // Add location bias if we have user coordinates and it's enabled
            if (showBiasToUserLocation && locationPermission === 'true' && storedLat && storedLon) {
                const lat = parseFloat(storedLat);
                const lng = parseFloat(storedLon);

                // Create location bias with a reasonable radius (5000 meters = 5km)
                request.locationBias = {
                    center: { lat, lng },
                    radius: 5000
                };
            }

            autocompleteServiceRef.current.getPlacePredictions(
                request,
                (predictions: any[], status: any) => {
                    if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                        // console.error('Autocomplete prediction error:', status);
                        setSearchResults([]);
                        setAutocompletePubsLoading(false);
                        if (onLoading) onLoading(false);
                        return;
                    }

                    // Filter to keep only establishment types that are likely pubs
                    const filteredPredictions = predictions.filter(prediction => {
                        const types = prediction.types || [];
                        return types.some((type: string) => 
                            ['bar', 'restaurant', 'establishment', 'food', 'point_of_interest'].includes(type)
                        );
                    });

                    setSearchResults(filteredPredictions);
                    setAutocompletePubsLoading(false);
                    if (onLoading) onLoading(false);
                }
            );
        }, 300),
        [isLoaded, showBiasToUserLocation, onLoading]
    );

    // Update search results when query changes
    useEffect(() => {
        if (searchQuery.trim().length >= 3) {
            setAutocompletePubsLoading(true);
            if (onLoading) onLoading(true);
            debouncedPlacesSearch(searchQuery);
        } else {
            setSearchResults([]);
        }
    }, [searchQuery, debouncedPlacesSearch, onLoading]);

    // Handle place selection
    const handlePlaceSelect = (place: any) => {
        if (!place || !place.place_id) return;
        
        setAutocompletePubsLoading(true);
        if (onLoading) onLoading(true);
        if (onError) onError(null);
        
        try {
            // Get detailed place information
            placesServiceRef.current.getDetails(
                { placeId: place.place_id, fields: ['name', 'formatted_address', 'geometry', 'place_id', 'address_components'] },
                (placeDetails: any, status: any) => {
                    if (status !== window.google.maps.places.PlacesServiceStatus.OK || !placeDetails) {
                        console.error('Place details error:', status);
                        onError?.('Failed to get pub details');
                        setAutocompletePubsLoading(false);
                        if (onLoading) onLoading(false);
                        return;
                    }
                    
                    // Extract latitude and longitude from place details
                    const lat = placeDetails.geometry?.location?.lat();
                    const lon = placeDetails.geometry?.location?.lng();

                    if (!lat || !lon) {
                        onError?.('Could not determine the location coordinates');
                        setAutocompletePubsLoading(false);
                        if (onLoading) onLoading(false);
                        return;
                    }

                    // Create pub result object
                    const pubResult: GooglePubResult = {
                        name: placeDetails.name,
                        address: placeDetails.formatted_address,
                        formatted_address: placeDetails.formatted_address,
                        google_place_id: placeDetails.place_id,
                        latitude: lat,
                        longitude: lon
                    };


                    // Pass the result to the callback
                    onPubSelect(pubResult);
                    setAutocompletePubsLoading(false);
                    if (onLoading) onLoading(false);
                }
            );
        } catch (error) {
            console.error('Error in place selection:', error);
            onError?.('Failed to process pub selection');
            setAutocompletePubsLoading(false);
            if (onLoading) onLoading(false);
        }
    };

    // Effect to set initial value
    useEffect(() => {
        if (initialQuery) {
            setSearchQuery(initialQuery);
        }
    }, [initialQuery]);

    // Show error if google maps failed to load
    if (loadError) {
        return (
            <Box sx={{ ...containerSx }}>
                <Typography color="error">
                    Error loading search service: {loadError.message}
                </Typography>
            </Box>
        );
    }

    // Show loading state if scripts are still loading
    if (!isLoaded) {
        return (
            <Box sx={{ ...containerSx, display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ ...containerSx }}>
            <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.description || '';
                }}
                filterOptions={(x) => x} // Disable built-in filtering
                loading={autocompletePubsLoading}
                loadingText="Searching..."
                noOptionsText={searchQuery.length < 3 ? "Type at least 3 characters to search..." : "No pubs found"}
                onChange={(_, value) => {
                    if (value && typeof value !== 'string') {
                        handlePlaceSelect(value);
                    }
                }}
                disabled={disabled}
                // Google TOS compliance: Custom Paper component with Google attribution
                PaperComponent={(props) => (
                    <Paper {...props} elevation={8}>
                        {props.children}
                        <Box sx={{ 
                            p: 0.5, 
                            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontSize: '10px',
                                    color: 'text.secondary',
                                    fontFamily: 'Roboto, Arial, sans-serif',
                                    mr: 2
                                }}
                            >
                                {GOOGLE_ATTRIBUTION}
                            </Typography>
                        </Box>
                    </Paper>
                )}
                renderInput={(params) => (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <TextField
                            {...params}
                            placeholder={placeholder}
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleChange}
                            autoFocus={autoFocus}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {autocompletePubsLoading ? <CircularProgress color="inherit" size={20} /> :
                                            <Box
                                                sx={{
                                                    backgroundColor: themeColors.midYellow,
                                                    borderRadius: '50%',
                                                    width: 33,
                                                    height: 33,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mr: 1
                                                }}
                                            >
                                                <SearchIcon sx={{ color: 'black', fontSize: 20 }} />
                                            </Box>}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                                sx: {
                                    ...inputSx
                                }
                            }}
                        />
                        {/* Google attribution positioned inside the search bar */}
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                position: 'absolute',
                                bottom: attributionBottom,
                                right: searchQuery ? attributionRightWithText : attributionRightEmpty, // Adjust position when clear button is present
                                fontSize: '9px',
                                color: 'text.secondary',
                                fontFamily: 'Roboto, Arial, sans-serif',
                                opacity: 0.7,
                                pointerEvents: 'none',
                                zIndex: 1
                            }}
                        >
                            {GOOGLE_ATTRIBUTION}
                        </Typography>
                    </Box>
                )}
                renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.place_id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOnIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Box>
                                <Typography variant="body1">{option.structured_formatting?.main_text || option.description}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {option.structured_formatting?.secondary_text}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            />
        </Box>
    );
};

export default PubSearchGoogle;